import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { useLang } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Dashboard from "@/pages/Dashboard";
import Learn from "@/pages/Learn";
import Practice from "@/pages/Practice";
import Quiz from "@/pages/Quiz";
import MockExam from "@/pages/MockExam";
import Results from "@/pages/Results";
import Signs from "@/pages/Signs";
import Coverage from "@/pages/Coverage";
import RoadMarkings from "@/pages/RoadMarkings";
import Pricing from "@/pages/Pricing";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Account from "@/pages/Account";

// ---------------------------------------------------------------------------
// Vipps OAuth callback — backend handles the code exchange server-side,
// but the browser lands here after redirect. We just show a spinner and
// then redirect to /dashboard once the cookie is set.
// ---------------------------------------------------------------------------
import { useEffect } from "react";
import api from "@/lib/api";

function VippsAuthCallback() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get("code");
    const state = params.get("state");
    if (!code) { window.location.href = "/"; return; }
    // Hit backend callback — it sets the session cookie and returns user
    api.get(`/auth/vipps/callback?code=${code}&state=${state || ""}`)
      .then(() => { window.location.href = "/dashboard"; })
      .catch(() => { window.location.href = "/?login_error=vipps"; });
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5B24] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 404 Not Found page
// ---------------------------------------------------------------------------
function NotFound() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-24 text-center">
        <div className="text-8xl font-bold text-gray-200 mb-4">404</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Page not found</h1>
        <p className="text-gray-500 mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link to="/dashboard">
          <Button className="bg-fjord hover:bg-fjord-700">
            <ArrowLeft className="w-4 h-4 me-2 rtl:rotate-180" />
            {t("backToDashboard")}
          </Button>
        </Link>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/auth/vipps/callback" element={<VippsAuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
      <Route path="/learn/:topicId" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
      <Route path="/practice" element={<ProtectedRoute><Practice /></ProtectedRoute>} />
      <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
      <Route path="/mock" element={<ProtectedRoute><MockExam /></ProtectedRoute>} />
      <Route path="/results/:attemptId" element={<ProtectedRoute><Results /></ProtectedRoute>} />
      <Route path="/signs" element={<ProtectedRoute><Signs /></ProtectedRoute>} />
      <Route path="/markings" element={<ProtectedRoute><RoadMarkings /></ProtectedRoute>} />
      <Route path="/coverage" element={<ProtectedRoute><Coverage /></ProtectedRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      {/* FIX: catch-all 404 — was showing blank white screen for unknown URLs */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;